'use client'

import { useContext, useMemo } from 'react'

import { useSimulatedSubscription } from '@/features/onboarding/hooks/use-tour-subscription'
import { TutorialPreviewContext } from '@/features/tutorial/lib/tutorial-preview-context'
import {
  BillingFeature,
  getMaxAccounts,
  getMaxSharedMembers,
  hasFeature,
  isAlgoAllowed,
  isAnalyticsTypeAllowed,
  isHedgeMechanismAllowed,
  isStreamKindAllowed,
  isTriggerConditionAllowed,
  isVenueAllowed,
} from '@/lib/billing/capabilities'
import { useSubscription } from '@/service/billing.hook'
import { AlgoType, HedgeMechanism } from '@/types/generated/algo'
import { StreamKind } from '@/types/generated/common/account'
import { AnalyticsType, TradeVenue } from '@/types/generated/common/types'
import { TriggerConditionType } from '@/types/generated/triggers'

/**
 * Feature-gating helpers resolved from the current user's `capabilities`
 * (`get_subscription`). This is the single place the UI should ask "is X allowed
 * on this plan?" — see the billing integration guide.
 *
 * Pre-gating here is permissive-when-uncertain: it only restricts once
 * capabilities have positively loaded. While the subscription is still loading,
 * or if its fetch failed (e.g. Stripe not configured), the helpers defer to the
 * backend — which is the real enforcement. That keeps a billing hiccup from
 * breaking core flows or flickering a paying user's Pro controls as locked. The
 * limits return `Infinity` in the same uncertain state so count-based gates
 * (e.g. "Add account") never falsely block.
 */
export function useCapabilities(options: { allowWhileLoading?: boolean } = {}) {
  const { allowWhileLoading = true } = options
  const { data, isError, isFetching } = useSubscription()
  // A tutorial's live preview shows a Pro widget as the real thing: with no capabilities and
  // the permissive fallback, every helper below allows.
  const preview = useContext(TutorialPreviewContext)
  // The welcome tour's QA dock (non-prod) plays the app as another plan.
  const simulated = useSimulatedSubscription()
  const capabilities = preview ? undefined : (simulated ?? data)?.capabilities

  return useMemo(() => {
    const present = !!capabilities
    const allowWithoutCapabilities = preview || allowWhileLoading || isError
    return {
      // A disabled unauthenticated query is idle rather than successful, so
      // `!isFetching` is also a resolved state. Consumers that would otherwise
      // start an entitled request with permissive placeholder capabilities can
      // wait on this flag without blocking signed-out/shared views forever.
      isCapabilitiesResolved: present || isError || !isFetching,
      // Nothing to decide with yet and no failure to fall back on: still loading, or the
      // query hasn't run (e.g. auth not hydrated). Lets a fail-closed caller show "checking"
      // rather than rendering every gate as locked.
      isCapabilitiesPending: !present && !preview && !isError,
      isAlgoAllowed: (algo: AlgoType) =>
        present ? isAlgoAllowed(capabilities, algo) : allowWithoutCapabilities,
      isHedgeMechanismAllowed: (mechanism: HedgeMechanism) =>
        present ? isHedgeMechanismAllowed(capabilities, mechanism) : allowWithoutCapabilities,
      isAnalyticsTypeAllowed: (type: AnalyticsType) =>
        present ? isAnalyticsTypeAllowed(capabilities, type) : allowWithoutCapabilities,
      isVenueAllowed: (venue: TradeVenue) =>
        present ? isVenueAllowed(capabilities, venue) : allowWithoutCapabilities,
      isTriggerConditionAllowed: (type: TriggerConditionType) =>
        present ? isTriggerConditionAllowed(capabilities, type) : allowWithoutCapabilities,
      isStreamKindAllowed: (kind: StreamKind) =>
        present ? isStreamKindAllowed(capabilities, kind) : allowWithoutCapabilities,
      hasFeature: (feature: BillingFeature) =>
        present ? hasFeature(capabilities, feature) : allowWithoutCapabilities,
      maxAccounts:
        present || !allowWithoutCapabilities
          ? getMaxAccounts(capabilities)
          : Number.POSITIVE_INFINITY,
      maxSharedMembers:
        present || !allowWithoutCapabilities
          ? getMaxSharedMembers(capabilities)
          : Number.POSITIVE_INFINITY,
    }
  }, [allowWhileLoading, capabilities, isError, isFetching, preview])
}
